import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { ServiceCategoryEnum } from "../../types/service.type";

import { GO_SERVICE_QUERY_KEYS } from "../queryKeys";
import { getReceivedRequests } from "../services/goHireService";

const REQUESTS_PAGE_LIMIT = 3;

interface UseReceivedRequestsParams {
  limit?: number;
  offset?: number;
  category?: ServiceCategoryEnum;
  sort?: Record<string, "ASC" | "DESC">;
  enabled?: boolean;
}

interface UseReceivedRequestsPaginatedParams extends Omit<UseReceivedRequestsParams, "offset"> {
  page?: number;
}

export const useReceivedRequestsPaginated = ({
  limit = REQUESTS_PAGE_LIMIT,
  page = 0,
  category,
  sort,
  enabled,
}: UseReceivedRequestsPaginatedParams = {}) => {
  return useQuery({
    queryKey: [...GO_SERVICE_QUERY_KEYS.receivedRequests(), { limit, page, category, sort }],
    queryFn: () => getReceivedRequests({ limit, offset: page * limit, category, sort }),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!category && enabled,
  });
};

export const useReceivedRequestsInfinite = ({
  limit = REQUESTS_PAGE_LIMIT,
  category,
  sort,
  enabled,
}: Omit<UseReceivedRequestsParams, "offset"> = {}) => {
  return useInfiniteQuery({
    queryKey: [...GO_SERVICE_QUERY_KEYS.receivedRequests(), { limit, category, sort }],
    queryFn: ({ pageParam = 0 }) => getReceivedRequests({ limit, offset: pageParam * limit, category, sort }),
    getNextPageParam: (lastPage, allPages) => {
      const fetchedCount = allPages.length * limit;
      return fetchedCount < lastPage.meta.totalItems ? allPages.length : undefined;
    },
    initialPageParam: 0,
    enabled: !!category && enabled,
  });
};
